import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AdoptedTag } from './seo/servedHead';
import { ServedHeadDefaults } from './seo/ServedHeadDefaults';
import Landing from './pages/Landing';
import { AuthProvider } from './contexts/AuthContext';
import { DashboardProvider } from './contexts/DashboardContext';
import { AdvancedAnalyticsProvider } from './contexts/AdvancedAnalyticsContext';
import PrivateRoute from './components/auth/PrivateRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import './App.css';

// Route-level code splitting. DYN-7: every one of these used to be a static
// import, so all 43 routes shipped in a single 619 kB gzipped bundle that
// downloaded before first paint on every page, including recharts and the
// whole dashboard widget tree for someone reading the pricing page.
//
// Landing stays a static import above. It is the entry point most visitors
// get, and making it a chunk only adds a round trip before the first paint.
const RestaurantLanding = React.lazy(() => import('./pages/RestaurantLanding'));
const MedSpaLanding = React.lazy(() => import('./pages/MedSpaLanding'));
const HomeServicesLanding = React.lazy(() => import('./pages/HomeServicesLanding'));
const FitnessLanding = React.lazy(() => import('./pages/FitnessLanding'));
const EcommerceLanding = React.lazy(() => import('./pages/EcommerceLanding'));
const HVACLanding = React.lazy(() => import('./pages/HVACLanding'));
const ConstructionLanding = React.lazy(() => import('./pages/ConstructionLanding'));
const RetailLanding = React.lazy(() => import('./pages/RetailLanding'));
// The three /use-cases pages are deliberately NOT routed. Wave 20, DYN-180.
// They sell things that do not exist: the Shopify sync endpoint returns 501 and
// no service writes Shopify data, WooCommerce has no code at all, the cash flow
// forecast is a sixty-day revenue projection, and the industry benchmarks are a
// hardcoded table. The files stay in git so they can come back when the
// integrations do. LandingPages.claims.test.ts fails if a route is added back.
const Login = React.lazy(() => import('./pages/Login'));
const Signup = React.lazy(() => import('./pages/Signup'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Integrations = React.lazy(() => import('./pages/Integrations'));
const Chatbot = React.lazy(() => import('./pages/Chatbot'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const Settings = React.lazy(() => import('./pages/Settings'));
const AuditLogs = React.lazy(() => import('./pages/settings/AuditLogs'));
const Billing = React.lazy(() => import('./pages/Billing'));
const BillingSuccess = React.lazy(() => import('./pages/BillingSuccess'));
const StripeCallback = React.lazy(() => import('./pages/StripeCallback'));
const CloverCallback = React.lazy(() => import('./pages/CloverCallback'));
const QuickBooksCallback = React.lazy(() => import('./pages/QuickBooksCallback'));
const ShopifyCallback = React.lazy(() => import('./pages/ShopifyCallback'));
const HubSpotCallback = React.lazy(() => import('./pages/HubSpotCallback'));
const GoogleAnalyticsCallback = React.lazy(() => import('./pages/GoogleAnalyticsCallback'));
const Help = React.lazy(() => import('./pages/Help'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const Contact = React.lazy(() => import('./pages/Contact'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));
const NotFound = React.lazy(() => import('./pages/NotFound'));


const RouteFallback: React.FC = () => (
  <div
    role="status"
    aria-live="polite"
    style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', width: '100%',
    }}
  >
    <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
      Loading
    </span>
    <div
      aria-hidden="true"
      style={{
        width: 36, height: 36, borderRadius: '50%',
        border: '3px solid rgba(99,102,241,0.18)', borderTopColor: '#6366F1',
        animation: 'dd-route-spin 0.7s linear infinite',
      }}
    />
    <style>{'@keyframes dd-route-spin{to{transform:rotate(360deg)}}'}</style>
  </div>
);

/**
 * DYN-260. The served head's tags, handed over so Helmet owns them.
 *
 * These are the DEFAULTS. A page that renders its own Helmet description
 * replaces this one rather than adding to it, which is measured in
 * `servedHead.test.tsx` rather than assumed, and a page that renders none keeps
 * this one. Empty in a test that does not supply them, and empty is correct:
 * there is no served head in jsdom unless the test builds one.
 */
interface AppProps {
  servedHead?: AdoptedTag[];
}

const App: React.FC<AppProps> = ({ servedHead = [] }) => {
  return (
    <HelmetProvider>
      <ServedHeadDefaults tags={servedHead} />
      <AuthProvider>
        <DashboardProvider>
          <AdvancedAnalyticsProvider>
            <Router>
            <ErrorBoundary>
            <div className="App">
              <React.Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/restaurants" element={<RestaurantLanding />} />
                <Route path="/med-spas" element={<MedSpaLanding />} />
                <Route path="/home-services" element={<HomeServicesLanding />} />
                <Route path="/fitness" element={<FitnessLanding />} />
                <Route path="/ecommerce" element={<EcommerceLanding />} />
                <Route path="/hvac" element={<HVACLanding />} />
                <Route path="/construction" element={<ConstructionLanding />} />
                <Route path="/retail" element={<RetailLanding />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/integrations" element={<Integrations />} />
                <Route path="/help" element={<Help />} />
                <Route path="/features" element={<Navigate to="/#features" replace />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />

                {/* Protected routes */}
                <Route element={<PrivateRoute />}>
                  <Route path="/integrations/stripe/callback" element={<StripeCallback />} />
                  <Route path="/integrations/clover/callback" element={<CloverCallback />} />
                  <Route path="/integrations/quickbooks/callback" element={<QuickBooksCallback />} />
                  <Route path="/integrations/shopify/callback" element={<ShopifyCallback />} />
                  <Route path="/integrations/hubspot/callback" element={<HubSpotCallback />} />
                  <Route path="/integrations/google_analytics/callback" element={<GoogleAnalyticsCallback />} />
                  <Route path="/onboarding" element={<Onboarding />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/analytics" element={<Dashboard />} />
                  <Route path="/chatbot" element={<Chatbot />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/billing/success" element={<BillingSuccess />} />
                  <Route path="/settings/*" element={<Settings />} />
                  <Route path="/settings/audit-logs" element={<AuditLogs />} />
                </Route>

                {/* 404 catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </React.Suspense>
            </div>
            </ErrorBoundary>
          </Router>
        </AdvancedAnalyticsProvider>
      </DashboardProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
